import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Mail, Folder, Headphones, Radio, Server, Activity, Copy, CheckCircle2 } from 'lucide-react';
import { CustomerService } from '../context/AppContext';
import { toast } from 'sonner';
interface ServiceDetailCardProps {
  service: CustomerService;
  index: number;
}
export function ServiceDetailCard({
  service,
  index
}: ServiceDetailCardProps) {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  const formatBytes = (mb?: number) => {
    if (mb === undefined) return '0 MB';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb} MB`;
  };
  const getStatusBadge = () => {
    if (service.serverType === 'sonicpanel' || service.serverType === 'centova') {
      const isOnline = service.streamStatus === 'online';
      return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1.5 ${isOnline ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`} data-id="element-1825">
          <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse-slow' : 'bg-red-500'}`} data-id="element-1826"></div>
          {isOnline ? 'Online' : 'Offline'}
        </span>;
    }
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${service.status === 'active' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : service.status === 'pending' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-slate-500/10 text-slate-400 border-slate-500/20'}`} data-id="element-1827">
        {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
      </span>;
  };
  // Generic Card
  if (!service.serverType || !['cpanel', 'sonicpanel', 'centova'].includes(service.serverType)) {
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.1
    }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-slate-700 transition-colors" data-id="element-1828">
        <div data-id="element-1829">
          <div className="flex items-center gap-3 mb-2" data-id="element-1830">
            <h3 className="text-lg font-bold text-white" data-id="element-1831">
              {service.packageName}
            </h3>
            {getStatusBadge()}
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400" data-id="element-1832">
            {service.domain && <>
                <span data-id="element-1833">{service.domain}</span>
                <span data-id="element-1834">•</span>
              </>}
            <span data-id="element-1835">
              Renews {new Date(service.nextRenewal).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4" data-id="element-1836">
          <span className="font-medium text-white" data-id="element-1837">
            ${service.price}/{service.billingCycle === 'yearly' ? 'yr' : 'mo'}
          </span>
          <button className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2" data-id="element-1838">
            Manage <ExternalLink size={14} data-id="element-1839" />
          </button>
        </div>
      </motion.div>;
  }
  // cPanel Card
  if (service.serverType === 'cpanel') {
    const diskPercent = service.diskLimitMb && service.diskUsageMb ? Math.min(100, Math.round(service.diskUsageMb / service.diskLimitMb * 100)) : 0;
    const bwPercent = service.bandwidthLimitMb && service.bandwidthUsedMb ? Math.min(100, Math.round(service.bandwidthUsedMb / service.bandwidthLimitMb * 100)) : 0;
    const webmailUrl = service.controlPanelUrl ? service.controlPanelUrl.replace(':2083', ':2096').replace(':2082', ':2096') : '#';
    const fileManagerUrl = service.controlPanelUrl ? `${service.controlPanelUrl}/cpsess/frontend/jupiter/filemanager/index.html` : '#';
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.1
    }} className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-colors" data-id="element-1840">
        <div className="p-6 border-b border-slate-800" data-id="element-1841">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-1842">
            <div data-id="element-1843">
              <div className="flex items-center gap-3 mb-2" data-id="element-1844">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-1845">
                  <Server size={16} className="text-orange-500" data-id="element-1846" />
                </div>
                <h3 className="text-lg font-bold text-white" data-id="element-1847">
                  {service.packageName}
                </h3>
                {getStatusBadge()}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400 ml-11" data-id="element-1848">
                {service.domain && <>
                    <span className="text-orange-400/80" data-id="element-1849">{service.domain}</span>
                    <span data-id="element-1850">•</span>
                  </>}
                <span data-id="element-1851">
                  Renews {new Date(service.nextRenewal).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="text-right" data-id="element-1852">
              <div className="font-medium text-white text-lg" data-id="element-1853">
                ${service.price}
                <span className="text-sm text-slate-400" data-id="element-1854">
                  /{service.billingCycle === 'yearly' ? 'yr' : 'mo'}
                </span>
              </div>
              {service.controlPanelUsername && <div className="text-xs text-slate-500 mt-1" data-id="element-1855">
                  User: {service.controlPanelUsername}
                </div>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1856">
            {/* Disk Usage */}
            <div data-id="element-1857">
              <div className="flex items-center justify-between text-sm mb-2" data-id="element-1858">
                <span className="text-slate-400" data-id="element-1859">Disk Usage</span>
                <span className="text-slate-300 font-medium" data-id="element-1860">
                  {formatBytes(service.diskUsageMb)} /{' '}
                  {formatBytes(service.diskLimitMb)}
                </span>
              </div>
              <div className="w-full bg-slate-900 rounded-full h-2" data-id="element-1861">
                <div className={`h-2 rounded-full ${diskPercent > 90 ? 'bg-red-500' : diskPercent > 75 ? 'bg-amber-500' : 'bg-orange-500'}`} style={{
                width: `${diskPercent}%`
              }} data-id="element-1862"></div>
              </div>
            </div>

            {/* Bandwidth */}
            <div data-id="element-1863">
              <div className="flex items-center justify-between text-sm mb-2" data-id="element-1864">
                <span className="text-slate-400" data-id="element-1865">Bandwidth</span>
                <span className="text-slate-300 font-medium" data-id="element-1866">
                  {formatBytes(service.bandwidthUsedMb)} /{' '}
                  {formatBytes(service.bandwidthLimitMb)}
                </span>
              </div>
              <div className="w-full bg-slate-900 rounded-full h-2" data-id="element-1867">
                <div className={`h-2 rounded-full ${bwPercent > 90 ? 'bg-red-500' : bwPercent > 75 ? 'bg-amber-500' : 'bg-orange-500'}`} style={{
                width: `${bwPercent}%`
              }} data-id="element-1868"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 p-4 px-6 flex flex-wrap items-center justify-between gap-4" data-id="element-1869">
          <div className="flex items-center gap-2 text-sm text-slate-400" data-id="element-1870">
            <Mail size={16} className="text-slate-500" data-id="element-1871" />
            <span data-id="element-1872">{service.emailAccounts || 0} Email Accounts</span>
          </div>
          <div className="flex flex-wrap items-center gap-3" data-id="element-1873">
            <a href={webmailUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2" data-id="element-1874">
              <Mail size={14} data-id="element-1875" /> Webmail
            </a>
            <a href={fileManagerUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2" data-id="element-1876">
              <Folder size={14} data-id="element-1877" /> Files
            </a>
            <a href={service.controlPanelUrl || '#'} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-orange-500/20" data-id="element-1878">
              cPanel Login <ExternalLink size={14} data-id="element-1879" />
            </a>
          </div>
        </div>
      </motion.div>;
  }
  // SonicPanel / Centova Card
  const isSonic = service.serverType === 'sonicpanel';
  const accentColor = isSonic ? 'cyan' : 'pink';
  const accentBg = isSonic ? 'bg-cyan-500/10' : 'bg-pink-500/10';
  const accentText = isSonic ? 'text-cyan-500' : 'text-pink-500';
  const accentBorderHover = isSonic ? 'hover:border-cyan-500/30' : 'hover:border-pink-500/30';
  const btnBg = isSonic ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-pink-600 hover:bg-pink-500';
  const btnShadow = isSonic ? 'shadow-cyan-500/20' : 'shadow-pink-500/20';
  const panelName = isSonic ? 'SonicPanel' : 'Centova Cast';
  // Generate some fake bars for the listener chart
  const listenerBars = [40, 65, 45, 80, 55];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.1
  }} className={`bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden ${accentBorderHover} transition-colors`} data-id="element-1880">
      <div className="p-6 border-b border-slate-800" data-id="element-1881">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6" data-id="element-1882">
          <div data-id="element-1883">
            <div className="flex items-center gap-3 mb-2" data-id="element-1884">
              <div className={`w-8 h-8 rounded-lg ${accentBg} flex items-center justify-center`} data-id="element-1885">
                <Radio size={16} className={accentText} data-id="element-1886" />
              </div>
              <h3 className="text-lg font-bold text-white" data-id="element-1887">
                {service.packageName}
              </h3>
              {getStatusBadge()}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400 ml-11" data-id="element-1888">
              <span data-id="element-1889">
                Renews {new Date(service.nextRenewal).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="text-right" data-id="element-1890">
            <div className="font-medium text-white text-lg" data-id="element-1891">
              ${service.price}
              <span className="text-sm text-slate-400" data-id="element-1892">
                /{service.billingCycle === 'yearly' ? 'yr' : 'mo'}
              </span>
            </div>
            {service.controlPanelUsername && <div className="text-xs text-slate-500 mt-1" data-id="element-1893">
                User: {service.controlPanelUsername}
              </div>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 ml-11" data-id="element-1894">
          {/* Listeners */}
          <div className="bg-slate-900 rounded-xl p-4 flex items-end justify-between" data-id="element-1895">
            <div data-id="element-1896">
              <p className="text-xs text-slate-500 mb-1" data-id="element-1897">Listeners</p>
              <div className="flex items-baseline gap-1" data-id="element-1898">
                <span className="text-2xl font-bold text-white" data-id="element-1899">
                  {service.currentListeners || 0}
                </span>
                <span className="text-xs text-slate-400" data-id="element-1900">
                  / {service.maxListeners || '∞'}
                </span>
              </div>
            </div>
            <div className="flex items-end gap-1 h-8" data-id="element-1901">
              {listenerBars.map((height, i) => <div key={i} className={`w-2 rounded-t-sm ${i === listenerBars.length - 1 ? accentBg.replace('/10', '') : 'bg-slate-700'}`} style={{
              height: `${height}%`
            }} data-id="element-1902"></div>)}
            </div>
          </div>

          {/* Bitrate */}
          <div className="bg-slate-900 rounded-xl p-4 flex flex-col justify-center" data-id="element-1903">
            <p className="text-xs text-slate-500 mb-1" data-id="element-1904">Bitrate</p>
            <div className="flex items-center gap-2" data-id="element-1905">
              <Activity size={16} className={accentText} data-id="element-1906" />
              <span className="text-lg font-bold text-white" data-id="element-1907">
                {service.bitrate || 128} kbps
              </span>
            </div>
          </div>

          {/* Stream URL */}
          <div className="bg-slate-900 rounded-xl p-4 flex flex-col justify-center" data-id="element-1908">
            <p className="text-xs text-slate-500 mb-1" data-id="element-1909">Stream URL</p>
            <div className="flex items-center gap-2" data-id="element-1910">
              <input type="text" readOnly value={service.streamUrl || 'Not configured'} className="bg-transparent text-sm text-slate-300 w-full focus:outline-none truncate" data-id="element-1911" />
              {service.streamUrl && <button onClick={() => copyToClipboard(service.streamUrl!)} className="text-slate-500 hover:text-white transition-colors" data-id="element-1912">
                  <Copy size={14} data-id="element-1913" />
                </button>}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/50 p-4 px-6 flex flex-wrap items-center justify-end gap-3" data-id="element-1914">
        {service.streamUrl && <a href={service.streamUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2" data-id="element-1915">
            <Headphones size={14} data-id="element-1916" /> Listen
          </a>}
        <a href={service.controlPanelUrl || '#'} target="_blank" rel="noopener noreferrer" className={`px-4 py-2 ${btnBg} text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-lg ${btnShadow}`} data-id="element-1917">
          {panelName} Login <ExternalLink size={14} data-id="element-1918" />
        </a>
      </div>
    </motion.div>;
}